import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, BarChart3, Table2 } from 'lucide-react';
import type { QueryResult } from '@/types';

// ═══════════════════════════════════════════
// Plan Node Types
// ═══════════════════════════════════════════

interface ParsedPlanNode {
  nodeType: string;
  relation?: string;
  alias?: string;
  cost: number;
  startupCost: number;
  actualTime?: number;
  startupTime?: number;
  estimatedRows: number;
  actualRows?: number;
  loops?: number;
  width: number;
  filter?: string;
  condition?: string;
  joinType?: string;
  sortKey?: string[];
  children: ParsedPlanNode[];
}

// ═══════════════════════════════════════════
// PostgreSQL JSON Plan Parser
// ═══════════════════════════════════════════

function parsePgPlanNode(node: Record<string, unknown>): ParsedPlanNode {
  const condition =
    (node['Index Cond'] as string | undefined) ??
    (node['Hash Cond'] as string | undefined) ??
    (node['Merge Cond'] as string | undefined) ??
    (node['Recheck Cond'] as string | undefined) ??
    (node['Join Filter'] as string | undefined);

  const children: ParsedPlanNode[] = [];
  if (Array.isArray(node['Plans'])) {
    for (const child of node['Plans']) {
      if (child && typeof child === 'object') {
        children.push(parsePgPlanNode(child as Record<string, unknown>));
      }
    }
  }

  return {
    nodeType: String(node['Node Type'] ?? 'Unknown'),
    relation: node['Relation Name'] as string | undefined,
    alias: node['Alias'] as string | undefined,
    cost: Number(node['Total Cost'] ?? 0),
    startupCost: Number(node['Startup Cost'] ?? 0),
    actualTime: node['Actual Total Time'] != null ? Number(node['Actual Total Time']) : undefined,
    startupTime: node['Actual Startup Time'] != null ? Number(node['Actual Startup Time']) : undefined,
    estimatedRows: Number(node['Plan Rows'] ?? 0),
    actualRows: node['Actual Rows'] != null ? Number(node['Actual Rows']) : undefined,
    loops: node['Actual Loops'] != null ? Number(node['Actual Loops']) : undefined,
    width: Number(node['Plan Width'] ?? 0),
    filter: node['Filter'] as string | undefined,
    condition,
    joinType: node['Join Type'] as string | undefined,
    sortKey: Array.isArray(node['Sort Key']) ? (node['Sort Key'] as string[]) : undefined,
    children,
  };
}

function parsePlanJSON(data: unknown): ParsedPlanNode | null {
  try {
    // PostgreSQL EXPLAIN (FORMAT JSON) returns an array with one object
    // that has a "Plan" key
    if (Array.isArray(data) && data.length > 0) {
      const first = data[0];
      if (first && typeof first === 'object' && 'Plan' in first) {
        const plan = (first as Record<string, unknown>)['Plan'];
        if (plan && typeof plan === 'object') {
          return parsePgPlanNode(plan as Record<string, unknown>);
        }
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════
// Parse from QueryResult
// ═══════════════════════════════════════════

/**
 * Detect whether a QueryResult looks like EXPLAIN output and parse it.
 * Returns null if it's not an EXPLAIN result.
 */
function parsePlanFromResult(result: QueryResult): ParsedPlanNode | null {
  if (!result.rows.length) return null;

  // PostgreSQL EXPLAIN (FORMAT JSON): single column "QUERY PLAN", rows contain JSON
  if (result.columns.length === 1 && result.columns[0].name === 'QUERY PLAN') {
    // The JSON plan might be split across rows (text lines) or in a single row
    const firstVal = result.rows[0]['QUERY PLAN'];
    if (typeof firstVal === 'string') {
      // Text format: rows are text lines of the plan
      // Try to parse concatenated text as JSON first
      const fullText = result.rows.map((r) => String(r['QUERY PLAN'] ?? '')).join('\n');
      try {
        const parsed: unknown = JSON.parse(fullText);
        return parsePlanJSON(parsed);
      } catch {
        // Not JSON — this is text-format EXPLAIN, don't parse it visually
        return null;
      }
    }
    // If the value is already an object/array (e.g. some drivers parse JSON automatically)
    if (typeof firstVal === 'object' && firstVal !== null) {
      if (Array.isArray(firstVal)) {
        return parsePlanJSON(firstVal);
      }
      return parsePlanJSON([firstVal]);
    }
  }

  return null;
}

// ═══════════════════════════════════════════
// MySQL EXPLAIN Detection
// ═══════════════════════════════════════════

function isMySQLExplain(result: QueryResult): boolean {
  if (result.columns.length < 4) return false;
  const names = new Set(result.columns.map((c) => c.name.toLowerCase()));
  return names.has('select_type') && names.has('table') && names.has('type');
}

function getMySQLAccessTypeColor(accessType: string | null): string {
  if (!accessType) return 'text-text-muted';
  const t = accessType.toLowerCase();
  // Full table scan — worst
  if (t === 'all') return 'text-accent-red';
  // Index-only operations — good
  if (t === 'eq_ref' || t === 'const' || t === 'system' || t === 'ref') return 'text-accent-green';
  // Range/index scans — moderate
  if (t === 'range' || t === 'index' || t === 'ref_or_null' || t === 'index_merge') return 'text-accent-yellow';
  // fulltext and unique_subquery — reasonable
  if (t === 'fulltext' || t === 'unique_subquery' || t === 'index_subquery') return 'text-accent-green';
  return 'text-text-secondary';
}

// ═══════════════════════════════════════════
// Compute total cost for coloring
// ═══════════════════════════════════════════

function computeTotalCost(node: ParsedPlanNode): number {
  return node.cost;
}

// ═══════════════════════════════════════════
// Plan Node Card
// ═══════════════════════════════════════════

function PlanNodeCard({ node, totalCost }: { node: ParsedPlanNode; totalCost: number }) {
  const costRatio = totalCost > 0 ? node.cost / totalCost : 0;
  const borderColor =
    costRatio > 0.7
      ? 'border-l-[#ef5350]'    // accent-red
      : costRatio > 0.3
        ? 'border-l-[#f0c556]'  // accent-yellow
        : 'border-l-[#3dd68c]'; // accent-green

  const rowMismatch =
    node.actualRows !== undefined &&
    node.estimatedRows > 0 &&
    (node.actualRows > node.estimatedRows * 10 || node.actualRows < node.estimatedRows / 10);

  return (
    <div className={`border-l-4 ${borderColor} bg-dbgo-panel rounded-r-lg px-3 py-2 mb-1 border border-l-4`}
         style={{ borderLeftWidth: '4px' }}>
      <div className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
        <span>{node.nodeType}</span>
        {node.joinType && (
          <span className="text-syn-keyword font-normal text-[10px]">({node.joinType})</span>
        )}
        {node.relation && (
          <span className="text-text-secondary font-normal ml-0.5">
            on <span className="text-syn-table">{node.relation}</span>
            {node.alias && node.alias !== node.relation && (
              <span className="text-text-muted ml-1">as {node.alias}</span>
            )}
          </span>
        )}
      </div>
      <div className="text-[11px] text-text-secondary mt-0.5 space-y-0.5">
        <div>
          Cost: <span className="font-mono text-text-primary">{node.startupCost.toFixed(2)}..{node.cost.toFixed(2)}</span>
        </div>
        {node.actualTime !== undefined && (
          <div>
            Time: <span className="font-mono text-text-primary">{node.actualTime.toFixed(2)}ms</span>
            {node.loops !== undefined && node.loops > 1 && (
              <span className="text-text-muted ml-1">({node.loops} loops)</span>
            )}
          </div>
        )}
        <div>
          Rows:{' '}
          <span className={`font-mono ${rowMismatch ? 'text-accent-yellow font-semibold' : 'text-text-primary'}`}>
            {node.actualRows !== undefined ? node.actualRows.toLocaleString() : '?'}
          </span>
          <span className="text-text-muted ml-1">(est: {node.estimatedRows.toLocaleString()})</span>
          {rowMismatch && <span className="text-accent-yellow ml-1" title="Large estimation mismatch">!!!</span>}
        </div>
        {node.filter && (
          <div className="text-text-muted truncate" title={node.filter}>
            Filter: <span className="text-syn-string">{node.filter}</span>
          </div>
        )}
        {node.condition && (
          <div className="text-text-muted truncate" title={node.condition}>
            Cond: <span className="text-syn-string">{node.condition}</span>
          </div>
        )}
        {node.sortKey && node.sortKey.length > 0 && (
          <div className="text-text-muted truncate" title={node.sortKey.join(', ')}>
            Sort: <span className="text-syn-number">{node.sortKey.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Plan Tree Renderer
// ═══════════════════════════════════════════

function PlanTree({ node, totalCost, depth = 0 }: { node: ParsedPlanNode; totalCost: number; depth?: number }) {
  const [collapsed, setCollapsed] = useState(false);
  const hasChildren = node.children.length > 0;

  return (
    <div style={{ marginLeft: depth > 0 ? 24 : 0 }}>
      <div className="flex items-start gap-1">
        {hasChildren && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="mt-1.5 p-0.5 text-text-muted hover:text-text-primary rounded transition-colors flex-shrink-0"
          >
            {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
        {!hasChildren && <div className="w-[16px] flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <PlanNodeCard node={node} totalCost={totalCost} />
        </div>
      </div>
      {!collapsed && hasChildren && (
        <div className="relative ml-[7px]">
          <div
            className="absolute top-0 bottom-2 w-px"
            style={{ left: 0, backgroundColor: 'var(--border-default)' }}
          />
          {node.children.map((child, i) => (
            <div key={i} className="relative pl-3 mt-1">
              {/* Horizontal connector line */}
              <div
                className="absolute top-3 h-px"
                style={{ left: 0, width: 12, backgroundColor: 'var(--border-default)' }}
              />
              <PlanTree node={child} totalCost={totalCost} depth={0} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// MySQL Explain Table
// ═══════════════════════════════════════════

function getField(row: Record<string, unknown>, key: string): string {
  const val = row[key];
  if (val == null) return '-';
  return String(val);
}

function getNumericField(row: Record<string, unknown>, key: string): string {
  const val = row[key];
  if (val == null) return '-';
  return Number(val).toLocaleString();
}

function MySQLExplainTable({ result }: { result: QueryResult }) {
  return (
    <div className="overflow-auto p-3">
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr className="text-text-secondary text-left border-b border-border-default">
            <th className="px-2 py-1.5 font-medium">id</th>
            <th className="px-2 py-1.5 font-medium">select_type</th>
            <th className="px-2 py-1.5 font-medium">table</th>
            <th className="px-2 py-1.5 font-medium">type</th>
            <th className="px-2 py-1.5 font-medium">possible_keys</th>
            <th className="px-2 py-1.5 font-medium">key</th>
            <th className="px-2 py-1.5 font-medium">rows</th>
            <th className="px-2 py-1.5 font-medium">Extra</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => {
            const accessType = row['type'] != null ? String(row['type']) : null;
            const colorClass = getMySQLAccessTypeColor(accessType);
            return (
              <tr key={i} className="border-b border-border-subtle hover:bg-dbgo-tab-hover transition-colors">
                <td className="px-2 py-1.5 font-mono text-text-primary">
                  {getField(row, 'id')}
                </td>
                <td className="px-2 py-1.5 text-syn-keyword">
                  {getField(row, 'select_type')}
                </td>
                <td className="px-2 py-1.5 text-syn-table font-mono">
                  {getField(row, 'table')}
                </td>
                <td className={`px-2 py-1.5 font-semibold ${colorClass}`}>
                  {accessType ?? '-'}
                </td>
                <td className="px-2 py-1.5 text-text-muted font-mono truncate max-w-[150px]">
                  {getField(row, 'possible_keys')}
                </td>
                <td className="px-2 py-1.5 text-accent-blue font-mono">
                  {getField(row, 'key')}
                </td>
                <td className="px-2 py-1.5 font-mono text-text-primary">
                  {getNumericField(row, 'rows')}
                </td>
                <td className="px-2 py-1.5 text-text-secondary truncate max-w-[200px]" title={getField(row, 'Extra')}>
                  {getField(row, 'Extra')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════
// Main QueryPlanView Component
// ═══════════════════════════════════════════

interface QueryPlanViewProps {
  result: QueryResult;
}

export function QueryPlanView({ result }: QueryPlanViewProps) {
  // Try PostgreSQL JSON plan first
  const pgPlan = useMemo(() => parsePlanFromResult(result), [result]);

  // Check for MySQL EXPLAIN table format
  const mysqlExplain = useMemo(() => isMySQLExplain(result), [result]);

  if (pgPlan) {
    const totalCost = computeTotalCost(pgPlan);
    return (
      <div className="flex-1 overflow-auto p-3">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={14} className="text-accent-blue" />
          <span className="text-xs font-semibold text-text-primary">Query Execution Plan</span>
          <div className="flex items-center gap-3 ml-4 text-[10px] text-text-muted">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#3dd68c]" /> &lt;30% cost
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#f0c556]" /> 30-70% cost
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#ef5350]" /> &gt;70% cost
            </span>
          </div>
        </div>
        <PlanTree node={pgPlan} totalCost={totalCost} />
      </div>
    );
  }

  if (mysqlExplain) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="flex items-center gap-2 px-3 pt-3 mb-2">
          <Table2 size={14} className="text-accent-blue" />
          <span className="text-xs font-semibold text-text-primary">MySQL Execution Plan</span>
          <div className="flex items-center gap-3 ml-4 text-[10px] text-text-muted">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#3dd68c]" /> const/ref/eq_ref
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#f0c556]" /> range/index
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#ef5350]" /> ALL (full scan)
            </span>
          </div>
        </div>
        <MySQLExplainTable result={result} />
      </div>
    );
  }

  // Not a recognized plan format — return null so the caller can fall back to grid
  return null;
}

/**
 * Check if the given result can be displayed as a visual plan.
 * Used by QueryEditor to decide whether to show the toggle button.
 */
export function canShowPlanView(result: QueryResult): boolean {
  // PostgreSQL JSON plan
  if (result.columns.length === 1 && result.columns[0].name === 'QUERY PLAN') {
    const firstVal = result.rows[0]?.['QUERY PLAN'];
    if (typeof firstVal === 'string') {
      const fullText = result.rows.map((r) => String(r['QUERY PLAN'] ?? '')).join('\n');
      try {
        const parsed: unknown = JSON.parse(fullText);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const first = parsed[0];
          if (first && typeof first === 'object' && 'Plan' in first) return true;
        }
      } catch {
        return false;
      }
    }
    if (typeof firstVal === 'object' && firstVal !== null) return true;
  }
  // MySQL EXPLAIN
  if (isMySQLExplain(result)) return true;
  return false;
}
